import { useNavigate } from "react-router-dom";
import { Column } from "../../../../core/components/Column";
import {
  CustomButton,
  SmallBackButton,
} from "../../../../core/components/CustomButton";
import { TextComponent } from "../../../../core/components/TextComponent";
import { Row } from "../../../../core/components/Row";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../core/store/store";
import rejected from "../../../../core/assets/rejected.png";
import accepted from "../../../../core/assets/accepted.png";
import defaultProfilePicture from "../../../../core/assets/profile.png";
import { onSendVerificationPhoto } from "../store/SettingsThunk";
import { CameraPublic } from "../../../../core/icons/Camera";
import { Picture } from "../../../../core/icons/Picture";
import { ModalSimple } from "../../../../core/components/Modal";
import { CheckVerifyBG } from "../../../../core/icons/CheckVerifyBG";

export const VerificationCodeScreen = () => {
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const filesFromRedux =
    useSelector((state: RootState) => state.onboarding.userInfo?.files) || [];
  const [file, setFile] = useState<File | null>(filesFromRedux[0] || null);
  const [previewUrl, setPreviewUrl] = useState<string>(defaultProfilePicture);
  const verificationCode = useSelector(
    (state: RootState) => state.settings.verificationCode,
  );
  const isLoading = useSelector((state: RootState) => state.settings.isLoading);
  const verificationStatus = useSelector(
    (state: RootState) => state.onboarding.getProfileConfig?.status_account,
  );
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const [isSubmittedModalOpen, setIsSubmittedModalOpen] = useState(false);
  const isLiveFaceRequired = verificationStatus === "live_face_required";

  const onBack = () => {
    navigate("/settings/statusVerification");
  };

  useEffect(() => {
    if (!file) {
      setPreviewUrl(defaultProfilePicture);
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [file]);

  const handleUploadPhoto = async () => {
    if (!file || !verificationCode) return;
    dispatch(
      onSendVerificationPhoto({
        file: file,
        code: verificationCode,
      }),
    ).then((action) => {
      if (onSendVerificationPhoto.fulfilled.match(action)) {
        const result = action.payload;
        if (result.requiresLiveness) {
          navigate("/settings/statusVerification/liveness", {
            state: result.livenessSession ? { result } : undefined,
          });
          return;
        }
        setIsSubmittedModalOpen(true);
      }
    });
  };

  const handleFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;
    if (!selectedFile.type.startsWith("image/")) return;
    setFile(selectedFile);
    event.target.value = "";
  };

  return (
    <>
      <Column className="bg-white p-8 max-md:overflow-y-auto max-md:pb-24">
        <SmallBackButton onClick={onBack} className="mb-4 md:mb-6" />
        <Row className=" mt-4 mb-10 block md:hidden">
          <TextComponent variant="h3" className="font-bold mr-2">
            Account verification:
          </TextComponent>
          <TextComponent
            variant="h3"
            className={`${isLiveFaceRequired ? "text-red-600" : "text-primary"} font-bold`}
          >
            {isLiveFaceRequired ? "Live face check required" : "Unverified"}
          </TextComponent>
        </Row>
        <Column
          crossAxisAlignment="center"
          className=" px-10 pb-10 self-center text-center"
        >
          {isLiveFaceRequired ? (
            <Column className="gap-4 md:w-3/4">
              <TextComponent variant="h4">
                Your account is suspended until you complete a live face check.
              </TextComponent>
              <TextComponent variant="h6">
                The live face must match your current main profile photo.
              </TextComponent>
              <CustomButton
                className="md:w-1/2 self-center"
                onClick={() =>
                  navigate("/settings/statusVerification/liveness", {
                    state: { flow: "admin-live-face" },
                  })
                }
              >
                Start live face check
              </CustomButton>
            </Column>
          ) : (
            <>
              <Column className="md:hidden  mb-5">
                <TextComponent variant="h4">
                  Your verification code
                </TextComponent>
                <TextComponent variant="h4" className=" font-bold">
                  {verificationCode}
                </TextComponent>
              </Column>
              <Column
                className="gap-4 w-full md:w-1/2"
                crossAxisAlignment="center"
              >
                <div className="relative w-[230px] h-[215px] flex items-center justify-center">
                  <img
                    src={previewUrl}
                    className="object-cover w-[180px] h-[180px] rounded-2xl"
                    alt="verification-preview"
                  />
                  <button
                    type="button"
                    className="absolute left-0 bottom-0 w-14 h-14 rounded-full bg-primary border-4 border-white text-white flex items-center justify-center"
                    onClick={() => cameraInputRef.current?.click()}
                    aria-label="Take photo"
                  >
                    <CameraPublic className="w-7 h-7" />
                  </button>
                  <button
                    type="button"
                    className="absolute right-0 bottom-0 w-14 h-14 rounded-full bg-primary border-4 border-white text-white flex items-center justify-center"
                    onClick={() => galleryInputRef.current?.click()}
                    aria-label="Choose from gallery"
                  >
                    <Picture />
                  </button>
                  <input
                    ref={cameraInputRef}
                    type="file"
                    accept="image/*"
                    capture="user"
                    className="hidden"
                    onChange={handleFileSelected}
                  />
                  <input
                    ref={galleryInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleFileSelected}
                  />
                </div>
              </Column>
              <Row className="hidden md:block lg:flex justify-center mt-4">
                <TextComponent variant="h1" className="font-bold mr-2">
                  Your verification code:
                </TextComponent>
                <TextComponent variant="h1" className="text-primary font-bold">
                  {verificationCode}
                </TextComponent>
              </Row>
              <TextComponent variant="h6" className="md:w-3/4 lg:w-1/2 mt-3">
                Notice: Any photos lacking both your face and the accompanying
                code on a piece of paper will not be accepted. Ensure you
                include the provided code, as demonstrated in the examples
                below, when submitting a photo of yourself.
              </TextComponent>
              <Row className=" flex justify-center mt-10 mb-4 gap-3">
                <img
                  src={rejected}
                  alt="Rejected example"
                  className="w-36 md:w-44 aspect-[3/4] object-cover rounded-xl bg-gray-100"
                />
                <img
                  src={accepted}
                  alt="Accepted example"
                  className="w-36 md:w-44 aspect-[3/4] object-cover rounded-xl bg-gray-100"
                />
              </Row>
              <TextComponent variant="h6" className="text-grayLight2 mb-3">
                Use camera or gallery icons above
              </TextComponent>
              {file && (
                <CustomButton
                  className="md:w-1/2"
                  onClick={handleUploadPhoto}
                  loading={isLoading}
                >
                  Submit photo
                </CustomButton>
              )}
            </>
          )}
        </Column>
      </Column>
      <ModalSimple
        title="Information"
        variant="secondary"
        actionExecuted={isSubmittedModalOpen}
        onClose={() => {
          setIsSubmittedModalOpen(false);
          navigate("/settings");
        }}
        icon={<CheckVerifyBG />}
        next
      >
        <TextComponent variant="h5">
          Thanks for submitting your photo! Please allow up to 24 hours for our
          staff to manually verify your profile.
        </TextComponent>
      </ModalSimple>
    </>
  );
};
